import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayrollAuditReport } from "../../../../store/thunks/reportsThunk";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const PayrollAuditReport = () => {
  const dispatch = useDispatch();
  const { data: auditData, loading, error } = useSelector(
    (state) => state.reports.payrollAudit || { data: null, loading: false, error: null }
  );

  // Filter state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  const [selectedClinics, setSelectedClinics] = useState([]);
  const [selectedClinicians, setSelectedClinicians] = useState([]);
  const [showOnlyMissing, setShowOnlyMissing] = useState(true);
  const [activeTab, setActiveTab] = useState("missing"); // missing, discrepancies, byClinician, byClinic

  // Get clinics and clinicians from Redux
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const users = useSelector((state) => state.users.users || []);
  const clinicians = users.filter((user) => user.userRole === "clinician");

  // Load initial data on mount
  useEffect(() => {
    handleSubmit();
  }, []);

  const handleSubmit = async () => {
    console.log(`[Payroll Audit Report] Fetching audit report for ${startDate} to ${endDate}`);

    try {
      await dispatch(
        fetchPayrollAuditReport({
          startDate,
          endDate,
          clinics: selectedClinics.length > 0 ? selectedClinics : undefined,
          clinicianIds: selectedClinicians.length > 0 ? selectedClinicians : undefined,
          showOnlyMissing,
        })
      ).unwrap();

      toast.success("Payroll audit report loaded successfully");
      console.log(`[Payroll Audit Report] Report loaded successfully`);
    } catch (err) {
      console.error(`[Payroll Audit Report] Error loading report:`, err);
      toast.error(err?.message || "Failed to load payroll audit report");
    }
  };

  const handleDownloadExcel = () => {
    if (!auditData) {
      toast.warning("No data available to export");
      return;
    }

    console.log(`[Payroll Audit Report] Exporting to Excel - Active tab: ${activeTab}`);

    let worksheet;
    let filename;

    if (activeTab === "missing") {
      // Export missing payroll entries
      const missingAppointments = (auditData.appointments || []).filter(
        (appt) => appt.auditStatus === "MISSING_PAYROLL"
      );

      const missingData = [
        ["Missing Payroll Entries"],
        [`Period: ${startDate} to ${endDate}`],
        [`Total Missing: ${missingAppointments.length}`],
        [],
        [
          "Appointment Date",
          "Clinician",
          "Client",
          "Service Type",
          "Invoice Amount",
          "Expected Payroll",
          "Missing Reason",
        ],
        ...missingAppointments.map((appt) => [
          new Date(appt.appointmentDate).toLocaleDateString(),
          appt.clinicianName,
          appt.clientName,
          appt.serviceType || "N/A",
          appt.invoiceAmount || 0,
          appt.expectedPayrollAmount || 0,
          appt.missingReason || "Unknown",
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(missingData);
      filename = `Payroll_Missing_Entries_${startDate}_to_${endDate}.xlsx`;
    } else if (activeTab === "discrepancies") {
      // Export discrepancy entries
      const discrepancyAppointments = (auditData.appointments || []).filter(
        (appt) => appt.auditStatus === "AMOUNT_DISCREPANCY"
      );

      const discrepancyData = [
        ["Payroll Amount Discrepancies"],
        [`Period: ${startDate} to ${endDate}`],
        [`Total Discrepancies: ${discrepancyAppointments.length}`],
        [],
        [
          "Appointment Date",
          "Clinician",
          "Client",
          "Expected Amount",
          "Actual Amount",
          "Variance",
        ],
        ...discrepancyAppointments.map((appt) => [
          new Date(appt.appointmentDate).toLocaleDateString(),
          appt.clinicianName,
          appt.clientName,
          appt.expectedPayrollAmount || 0,
          appt.payrollAmount || 0,
          appt.amountVariance || 0,
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(discrepancyData);
      filename = `Payroll_Discrepancies_${startDate}_to_${endDate}.xlsx`;
    } else if (activeTab === "byClinician") {
      // Export by clinician
      const clinicianData = [
        ["Missing Payroll by Clinician"],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ["Clinician", "Missing Count", "Potential Unpaid Amount"],
        ...(auditData.missingByClinician || []).map((item) => [
          item.clinicianName,
          item.missingCount,
          item.potentialUnpaidAmount,
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(clinicianData);
      filename = `Payroll_Missing_By_Clinician_${startDate}_to_${endDate}.xlsx`;
    } else if (activeTab === "byClinic") {
      // Export by clinic
      const clinicData = [
        ["Missing Payroll by Clinic"],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ["Clinic", "Missing Count", "Potential Unpaid Amount"],
        ...(auditData.missingByClinic || []).map((item) => [
          item.clinicName,
          item.missingCount,
          item.potentialUnpaidAmount,
        ]),
      ];

      worksheet = XLSX.utils.aoa_to_sheet(clinicData);
      filename = `Payroll_Missing_By_Clinic_${startDate}_to_${endDate}.xlsx`;
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    XLSX.writeFile(workbook, filename);

    toast.success("Excel file downloaded successfully");
    console.log(`[Payroll Audit Report] Excel exported: ${filename}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleClinicToggle = (clinicId) => {
    setSelectedClinics((prev) =>
      prev.includes(clinicId)
        ? prev.filter((id) => id !== clinicId)
        : [...prev, clinicId]
    );
  };

  const handleClinicianToggle = (clinicianId) => {
    setSelectedClinicians((prev) =>
      prev.includes(clinicianId)
        ? prev.filter((id) => id !== clinicianId)
        : [...prev, clinicianId]
    );
  };

  // Calculate filtered appointment lists
  const missingAppointments = (auditData?.appointments || []).filter(
    (appt) => appt.auditStatus === "MISSING_PAYROLL"
  );

  const discrepancyAppointments = (auditData?.appointments || []).filter(
    (appt) => appt.auditStatus === "AMOUNT_DISCREPANCY"
  );

  const summary = auditData?.summary || {
    totalAppointments: 0,
    appointmentsWithPayroll: 0,
    appointmentsMissingPayroll: 0,
    appointmentsWithDiscrepancy: 0,
    totalPotentialUnpaid: 0,
    totalDiscrepancyAmount: 0,
    reconciliationPercentage: 100,
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payroll Audit Report
          </h1>
          <p className="text-gray-600">
            Identify appointments missing payroll entries to ensure no clinician
            goes unpaid
          </p>
        </div>

        {/* Alert Banner */}
        {auditData && !loading && (
          <div>
            {summary.appointmentsMissingPayroll > 0 ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-red-800">
                      Missing Payroll Entries Detected
                    </h3>
                    <p className="text-red-700 mt-1">
                      {summary.appointmentsMissingPayroll} appointment
                      {summary.appointmentsMissingPayroll !== 1 ? "s" : ""} found
                      without payroll entries. Potential unpaid amount:{" "}
                      <strong>{formatCurrency(summary.totalPotentialUnpaid)}</strong>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-green-800">
                      All Appointments Reconciled
                    </h3>
                    <p className="text-green-700 mt-1">
                      100% of billable appointments have corresponding payroll
                      entries. No missing payroll detected.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filter Panel */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Clinics Multi-Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinics (Optional)
            </label>
            <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto bg-white">
              {clinics.length === 0 ? (
                <p className="text-gray-500 text-sm">No clinics available</p>
              ) : (
                clinics.map((clinic) => (
                  <label
                    key={clinic._id}
                    className="flex items-center space-x-2 py-1 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClinics.includes(clinic._id)}
                      onChange={() => handleClinicToggle(clinic._id)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {clinic.clinicName}
                    </span>
                  </label>
                ))
              )}
            </div>
            {selectedClinics.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedClinics.length} clinic(s) selected
              </p>
            )}
          </div>

          {/* Clinicians Multi-Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinicians (Optional)
            </label>
            <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto bg-white">
              {clinicians.length === 0 ? (
                <p className="text-gray-500 text-sm">No clinicians available</p>
              ) : (
                clinicians.map((clinician) => (
                  <label
                    key={clinician._id}
                    className="flex items-center space-x-2 py-1 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClinicians.includes(clinician._id)}
                      onChange={() => handleClinicianToggle(clinician._id)}
                      className="rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {clinician.firstName} {clinician.lastName}
                    </span>
                  </label>
                ))
              )}
            </div>
            {selectedClinicians.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedClinicians.length} clinician(s) selected
              </p>
            )}
          </div>

          {/* Show Only Missing Toggle */}
          <div className="mb-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyMissing}
                onChange={(e) => setShowOnlyMissing(e.target.checked)}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Show only appointments with missing payroll or discrepancies
              </span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Loading..." : "Run Audit"}
            </button>
            <button
              onClick={handleDownloadExcel}
              disabled={!auditData || loading}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">
              Error loading report: {error.message || "Unknown error"}
            </p>
          </div>
        )}

        {/* Results */}
        {auditData && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Total Appointments</p>
                <p className="text-2xl font-bold text-blue-600">
                  {summary.totalAppointments}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">With Payroll</p>
                <p className="text-2xl font-bold text-green-600">
                  {summary.appointmentsWithPayroll}
                </p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Missing Payroll</p>
                <p className="text-2xl font-bold text-red-600">
                  {summary.appointmentsMissingPayroll}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">
                  Reconciliation %
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {summary.reconciliationPercentage}%
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab("missing")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "missing"
                      ? "border-red-600 text-red-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Missing Payroll ({missingAppointments.length})
                </button>
                <button
                  onClick={() => setActiveTab("discrepancies")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "discrepancies"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Discrepancies ({discrepancyAppointments.length})
                </button>
                <button
                  onClick={() => setActiveTab("byClinician")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "byClinician"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  By Clinician
                </button>
                <button
                  onClick={() => setActiveTab("byClinic")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === "byClinic"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  By Clinic
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "missing" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Missing Payroll Entries
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clinician
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Service
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expected Payroll
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {missingAppointments.map((appt, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(appt.appointmentDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {appt.clinicianName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {appt.clientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {appt.serviceType || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(appt.invoiceAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                            {formatCurrency(appt.expectedPayrollAmount)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {appt.missingReason || "Unknown"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {missingAppointments.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No missing payroll entries found
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "discrepancies" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Amount Discrepancies
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clinician
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expected Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actual Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {discrepancyAppointments.map((appt, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(appt.appointmentDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {appt.clinicianName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {appt.clientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(appt.expectedPayrollAmount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(appt.payrollAmount)}
                          </td>
                          <td
                            className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                              appt.amountVariance > 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {formatCurrency(appt.amountVariance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {discrepancyAppointments.length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No amount discrepancies found
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "byClinician" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Missing Payroll by Clinician
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clinician
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Missing Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Potential Unpaid Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(auditData.missingByClinician || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.clinicianName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.missingCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                            {formatCurrency(item.potentialUnpaidAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(auditData.missingByClinician || []).length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No missing payroll entries by clinician
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "byClinic" && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Missing Payroll by Clinic
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Clinic
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Missing Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Potential Unpaid Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(auditData.missingByClinic || []).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.clinicName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.missingCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                            {formatCurrency(item.potentialUnpaidAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(auditData.missingByClinic || []).length === 0 && (
                    <p className="text-center text-gray-500 py-8">
                      No missing payroll entries by clinic
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Data Message */}
        {!auditData && !loading && !error && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">
              Click "Run Audit" to check for missing payroll entries
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollAuditReport;
